import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  createVTScadaConnection,
  deleteVTScadaConnection,
  listVTScadaConnections,
  testVTScadaConnection,
  updateVTScadaConnection,
} from "../../../../api";
import { useNotify } from "../../../../ToastContext";
import { usePermission } from "../../../../UserContext";
import plusIcon from "../../../../assets/plus.svg";
import xmarkIcon from "../../../../assets/xmark.svg";

const ROW_BUTTON_STYLE = { height: 28, padding: "0 10px", borderRadius: 8 };
const FOCUSABLE = "button:not([disabled]), input:not([disabled])";

const errorMessage = (error, fallback) => {
  const message = String(error?.message || error).trim();
  return message || fallback;
};

const keepFocusInModal = (event) => {
  if (event.key !== "Tab") return;
  const focusable = Array.from(event.currentTarget.querySelectorAll(FOCUSABLE));
  if (focusable.length === 0) {
    event.preventDefault();
    event.currentTarget.focus();
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (document.activeElement === event.currentTarget) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
  } else if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

const handleModalKeyDown = (event, busy, onClose) => {
  if (event.key === "Escape" && !busy) {
    event.preventDefault();
    onClose();
    return;
  }
  keepFocusInModal(event);
};

const RealmURLHelp = () => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);

  const handleKeyDown = (event) => {
    if (event.key !== "Escape" || !open) return;
    event.preventDefault();
    event.stopPropagation();
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <span
      className="vtscadaRealmHelp"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
      onKeyDown={handleKeyDown}
    >
      <button
        ref={triggerRef}
        className="vtscadaRealmHelpButton"
        type="button"
        aria-label="About Realm URL"
        aria-expanded={open}
        aria-controls="vtscada-realm-url-help"
        onClick={() => setOpen((current) => !current)}
      >
        i
      </button>
      {open && (
        <span className="vtscadaRealmHelpTooltip" id="vtscada-realm-url-help" role="tooltip">
          The HTTPS address of this application&apos;s VTScada Internet Realm, including the realm name. For example,
          {" "}<code>https://scada.example.com/RealmName</code>. Copy the base of its VTScada Anywhere URL without
          {" "}<code>/anywhere</code>, find it in VAM under Thin Client / Server Setup → Realms, or ask your VTScada
          administrator. If no realm exists, an administrator must configure one and enable Remote Data access.
        </span>
      )}
    </span>
  );
};

const ConnectionModal = ({ connection, onClose, onSaved }) => {
  const editing = Boolean(connection);
  const [realmURL, setRealmURL] = useState(connection?.base_url || "");
  const [username, setUsername] = useState(connection?.username || "");
  const [password, setPassword] = useState("");
  const [queryLimitsVerified, setQueryLimitsVerified] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const notify = useNotify();

  const trimmedUsername = username.trim();
  const changed = editing && (
    trimmedUsername !== connection.username || password.length > 0
  );
  const canSubmit = editing
    ? trimmedUsername.length > 0 && changed
    : realmURL.trim().length > 0
      && trimmedUsername.length > 0
      && password.length > 0
      && queryLimitsVerified;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting || !canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      if (editing) {
        const patch = {};
        if (trimmedUsername !== connection.username) patch.username = trimmedUsername;
        if (password) patch.password = password;
        await updateVTScadaConnection(connection.id, patch);
      } else {
        await createVTScadaConnection({
          base_url: realmURL.trim(),
          username: trimmedUsername,
          password,
          query_limits_verified: true,
        });
      }
      notify({
        type: "success",
        title: editing ? "Credentials updated" : "VTScada configured",
        message: editing ? "The saved credentials were updated." : "The VTScada realm was added.",
      });
      await onSaved();
      onClose();
    } catch (submitError) {
      setError(errorMessage(submitError, editing ? "Could not update credentials." : "Could not add connection."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="gwOverlay" onClick={() => !submitting && onClose()}>
      <form
        className="gwModal"
        style={{ width: 520 }}
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
        onKeyDown={(event) => handleModalKeyDown(event, submitting, onClose)}
        autoComplete="off"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vtscada-connection-title"
      >
        <div className="gwModalHeader">
          <div style={{ minWidth: 0 }}>
            <div className="gwModalTitle" id="vtscada-connection-title">
              {editing ? "VTScada credentials" : "Connect VTScada"}
            </div>
            <div className="gwModalSubtitle" title={editing ? connection.base_url : undefined}>
              {editing ? connection.base_url : "Add read-only access to one VTScada realm."}
            </div>
          </div>
          <button className="gwModalClose" type="button" onClick={onClose} disabled={submitting} aria-label="Close">
            <img src={xmarkIcon} alt="" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
          </button>
        </div>
        <div className="gwModalBody">
          {!editing && (
            <div className="gwField">
              <div className="vtscadaRealmLabel">
                <label className="gwLabel" htmlFor="vtscada-realm-url">Realm URL</label>
                <RealmURLHelp />
              </div>
              <input
                id="vtscada-realm-url"
                className="gwInput"
                type="url"
                placeholder="https://scada.example.com/RealmName"
                value={realmURL}
                onChange={(event) => setRealmURL(event.target.value)}
                autoComplete="url"
                autoFocus
                required
              />
              <div className="gwHint">Example: https://scada.example.com/RealmName</div>
            </div>
          )}
          <div className="gwFieldRow">
            <div className="gwField">
              <label className="gwLabel" htmlFor="vtscada-username">Username</label>
              <input
                id="vtscada-username"
                className="gwInput"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoComplete="off"
                autoFocus={editing}
                required
              />
            </div>
            <div className="gwField">
              <label className="gwLabel" htmlFor="vtscada-password">
                {editing ? "New password" : "Password"}
              </label>
              <input
                id="vtscada-password"
                className="gwInput"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                onInput={(event) => {
                  if (event.target.value !== password) setPassword(event.target.value);
                }}
                autoComplete="off"
                placeholder={editing ? "Leave blank to keep current" : ""}
                required={!editing}
              />
            </div>
          </div>
          {!editing && (
            <label
              className="gwHint"
              htmlFor="vtscada-query-limits-verified"
              style={{ display: "flex", alignItems: "flex-start", gap: 8, margin: 0 }}
            >
              <input
                id="vtscada-query-limits-verified"
                type="checkbox"
                checked={queryLimitsVerified}
                onChange={(event) => setQueryLimitsVerified(event.target.checked)}
                required
                style={{ marginTop: 2, accentColor: "var(--gw-accent)", flexShrink: 0 }}
              />
              <span>
                I confirm a VTScada administrator set SQLPaginationEnabled=TRUE,
                {" "}SQLQueryMaxResultRows&gt;=10000, and SQLQueryMaxResultCells&gt;=1000000.
              </span>
            </label>
          )}
          {error && <div className="gwHint" role="alert" style={{ color: "var(--gw-danger)" }}>{error}</div>}
        </div>
        <div className="gwModalFooter">
          <button className="gwGhostBtn" type="button" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="gwPrimaryBtn" type="submit" disabled={submitting || !canSubmit}>
            {submitting ? "Saving..." : editing ? "Save credentials" : "Connect"}
          </button>
        </div>
      </form>
    </div>
  );
};

export const VTScadaConnectionsPage = () => {
  const canConfigure = usePermission("configure");
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [dialog, setDialog] = useState(null);
  const [testing, setTesting] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const addButtonRef = useRef(null);
  const retryButtonRef = useRef(null);
  const deleteDialogRef = useRef(null);
  const returnFocusRef = useRef(null);
  const notify = useNotify();
  const editing = dialog?.kind === "edit" ? dialog.connection : null;
  const deleteTarget = dialog?.kind === "delete" ? dialog.connection : null;

  const restoreFocus = useCallback(() => {
    const previous = returnFocusRef.current;
    returnFocusRef.current = null;
    const focus = () => {
      const target = [previous, addButtonRef.current, retryButtonRef.current]
        .find((candidate) => candidate?.isConnected && !candidate.disabled);
      target?.focus();
    };
    if (window.requestAnimationFrame) window.requestAnimationFrame(focus);
    else window.setTimeout(focus, 0);
  }, []);

  const openDialog = (event, kind, connection = null) => {
    returnFocusRef.current = event.currentTarget;
    setDialog({ kind, connection });
  };

  const closeDialog = () => {
    setDialog(null);
    restoreFocus();
  };

  useEffect(() => {
    if (deleting) deleteDialogRef.current?.focus();
  }, [deleting]);

  const loadConnections = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      setConnections((await listVTScadaConnections()) || []);
    } catch (error) {
      setLoadError(errorMessage(error, "Could not load VTScada connections."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  const runTest = async (connection) => {
    setTesting(connection.id);
    try {
      await testVTScadaConnection(connection.id);
      notify({
        type: "success",
        title: "Connection OK",
        message: "VTScada responded.",
      });
    } catch (error) {
      notify({ type: "error", title: "Test failed", message: errorMessage(error, "VTScada did not respond.") });
    } finally {
      setTesting(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await deleteVTScadaConnection(deleteTarget.id);
      notify({ type: "success", title: "Removed", message: "The VTScada connection was removed." });
      await loadConnections();
      setDialog(null);
      restoreFocus();
    } catch (error) {
      notify({ type: "error", title: "Remove failed", message: errorMessage(error, "Could not remove connection.") });
    } finally {
      setDeleting(false);
    }
  };

  const hasConnections = connections.length > 0;
  const actionsDisabled = !canConfigure || loading || Boolean(loadError) || Boolean(deleteTarget);
  const actionTitle = !canConfigure
    ? "Requires configure permission"
    : loadError
      ? "Retry loading connections first"
      : loading
        ? "Refreshing connections"
        : undefined;

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">VTScada</div>
            <div className="gwPageSubtitle">
              {hasConnections
                ? `${connections.length} realm${connections.length === 1 ? "" : "s"} configured`
                : "Read-only access to selected VTScada data."}
            </div>
          </div>
          <button
            ref={addButtonRef}
            className="gwPrimaryBtn"
            type="button"
            onClick={(event) => openDialog(event, "create")}
            disabled={actionsDisabled}
            title={actionTitle}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
            <span>Add connection</span>
          </button>
        </div>

        {loading && !hasConnections && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--gw-muted)", fontSize: 13 }}>Loading connections...</div>
        )}

        {!loading && loadError && (
          <div className="gwEmptyState" role="alert">
            <div className="gwEmptyTitle">Could not load connections</div>
            <div className="gwEmptyBody">{loadError}</div>
            <div className="gwEmptyActions">
              <button ref={retryButtonRef} className="gwGhostBtn" type="button" onClick={loadConnections}>Retry</button>
            </div>
          </div>
        )}

        {!loading && !loadError && !hasConnections && (
          <div className="gwEmptyState">
            <div className="gwEmptyTitle">No VTScada connections</div>
            <div className="gwEmptyBody">Add one realm to begin read-only data access.</div>
          </div>
        )}

        {hasConnections && !loadError && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">Connections</div>
            <div className="gwHint" style={{ margin: "2px 0 8px" }}>
              While connected, Control Seat polls current values for imported tags (read-only); history and alarms are read on demand.
            </div>
            <div className="gwCardSectionBody" style={{ padding: 0 }}>
              {connections.map((connection) => (
                <div className="umUserRow" style={{ flexWrap: "wrap" }} key={connection.id}>
                  <div
                    aria-hidden="true"
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "rgba(120,170,255,0.95)",
                      background: "rgba(80,140,255,0.12)",
                      border: "1px solid rgba(80,140,255,0.25)",
                    }}
                  >
                    VT
                  </div>
                  <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                    <span
                      className="umUserName"
                      title={connection.base_url}
                      style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {connection.base_url}
                    </span>
                    <div className="umUserMeta">
                      {connection.username}
                      {!connection.has_password && <span style={{ color: "var(--gw-danger)" }}> &middot; password missing</span>}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <button
                      className="gwGhostBtn"
                      style={ROW_BUTTON_STYLE}
                      type="button"
                      onClick={() => runTest(connection)}
                      disabled={actionsDisabled || testing !== null}
                      title={actionTitle}
                      aria-label={`Test ${connection.base_url}`}
                    >
                      {testing === connection.id ? "Testing..." : "Test"}
                    </button>
                    <button
                      className="gwGhostBtn"
                      style={ROW_BUTTON_STYLE}
                      type="button"
                      onClick={(event) => openDialog(event, "edit", connection)}
                      disabled={actionsDisabled}
                      title={actionTitle}
                      aria-label={`Edit credentials for ${connection.base_url}`}
                    >
                      Credentials
                    </button>
                    <button
                      className="gwGhostBtn"
                      style={{ ...ROW_BUTTON_STYLE, color: "var(--gw-danger)" }}
                      type="button"
                      onClick={(event) => openDialog(event, "delete", connection)}
                      disabled={actionsDisabled}
                      title={actionTitle}
                      aria-label={`Remove ${connection.base_url}`}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {dialog?.kind === "create" && (
        <ConnectionModal onClose={closeDialog} onSaved={loadConnections} />
      )}
      {editing && (
        <ConnectionModal connection={editing} onClose={closeDialog} onSaved={loadConnections} />
      )}

      {deleteTarget && (
        <div className="gwOverlay" onClick={() => !deleting && closeDialog()}>
          <div
            ref={deleteDialogRef}
            className="gwModal"
            style={{ width: 420 }}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => handleModalKeyDown(event, deleting, closeDialog)}
            role="dialog"
            aria-modal="true"
            aria-busy={deleting}
            aria-labelledby="remove-vtscada-title"
            tabIndex={-1}
          >
            <div className="gwModalHeader">
              <div>
                <div className="gwModalTitle" id="remove-vtscada-title">Remove VTScada connection</div>
                <div className="gwModalSubtitle">Selected signals will be disconnected.</div>
              </div>
              <button className="gwModalClose" type="button" onClick={closeDialog} disabled={deleting} aria-label="Close">
                <img src={xmarkIcon} alt="" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
              </button>
            </div>
            <div className="gwModalBody">
              <p style={{ fontSize: 13, color: "var(--gw-text-secondary)", margin: 0 }}>
                Remove <strong>{deleteTarget.base_url}</strong>? Any selected VTScada signals will lose their source.
              </p>
            </div>
            <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
              <button className="gwGhostBtn" type="button" onClick={closeDialog} disabled={deleting} autoFocus>Cancel</button>
              <button className="gwDangerBtn" type="button" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Removing..." : "Remove connection"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
